package fr.frux.feature.treedetail.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Compare
import androidx.compose.material.icons.outlined.ExpandLess
import androidx.compose.material.icons.outlined.ExpandMore
import androidx.compose.material.icons.outlined.Flag
import androidx.compose.material.icons.outlined.Info
import androidx.compose.material.icons.outlined.Person
import androidx.compose.material.icons.outlined.Shield
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.core.designsystem.theme.fruxColors
import fr.frux.feature.treedetail.resources.Res
import fr.frux.feature.treedetail.resources.treedetail_confidence_disclaimer
import fr.frux.feature.treedetail.resources.treedetail_confidence_label
import fr.frux.feature.treedetail.resources.treedetail_confidence_lookalike_collapse
import fr.frux.feature.treedetail.resources.treedetail_confidence_lookalike_expand
import fr.frux.feature.treedetail.resources.treedetail_confidence_lookalike_title
import fr.frux.feature.treedetail.resources.treedetail_confidence_owner_note
import fr.frux.feature.treedetail.resources.treedetail_confidence_owner_note_disputed
import fr.frux.feature.treedetail.resources.treedetail_confidence_report_cta
import fr.frux.feature.treedetail.resources.treedetail_confidence_subtitle_disputed
import fr.frux.shared.domain.model.Certainty
import fr.frux.shared.domain.model.SpeciesLookalike
import org.jetbrains.compose.resources.stringResource

/**
 * Bloc « Confiance » de la fiche arbre (spec Fiche arbre, zone 2 — ADR 0015) : libellé de
 * certitude, jauge 3 segments, action « Signaler… », info préparation (niveau 2), ligne repliable
 * « Ne pas confondre avec… » (niveau 3) et mention permanente. Les bandeaux niveau 4 et
 * « contesté » restent hors du bloc, en tête de fiche.
 *
 * Plus de compteur ni de « Je confirme » (ADR 0025) : l'identification ne se vote pas. Un doute se
 * consigne par un signalement, un accord ne se consigne pas.
 *
 * @param lookalikes Sosies du niveau 3 (info repliée) ; liste vide quand le niveau 4 (bandeau
 *   rouge) est actif — les deux dérivent du même `TreeHazard`, jamais simultanés.
 */
@Composable
fun ConfidenceBlock(
    certainty: Certainty,
    certaintyLabel: String,
    disputed: Boolean,
    isOwnTree: Boolean,
    hasReportActions: Boolean,
    isReportPending: Boolean,
    onReportClick: () -> Unit,
    preparationLabel: String?,
    lookalikes: List<SpeciesLookalike>,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier =
            modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(28.dp))
                .background(MaterialTheme.colorScheme.surfaceVariant)
                .padding(20.dp),
    ) {
        SectionLabel(icon = Icons.Outlined.Shield, text = stringResource(Res.string.treedetail_confidence_label))
        Spacer(Modifier.height(FruxSpacing.sm))
        Text(certaintyLabel, style = MaterialTheme.typography.titleLarge)
        if (disputed) {
            Text(
                text = stringResource(Res.string.treedetail_confidence_subtitle_disputed),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        Spacer(Modifier.height(10.dp))
        CertaintyGauge(certainty)

        if (preparationLabel != null) {
            Spacer(Modifier.height(14.dp))
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                Icon(
                    imageVector = Icons.Outlined.Info,
                    contentDescription = null,
                    modifier = Modifier.size(20.dp),
                    tint = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Text(
                    text = preparationLabel,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }

        // Un visiteur sans action de signalement n'a plus rien a faire ici depuis le retrait du
        // vote (ADR 0025) : ni bloc d'actions, ni l'espace qui le precedait.
        if (isOwnTree) {
            Spacer(Modifier.height(14.dp))
            OwnerActions(
                hasReportActions = hasReportActions,
                isReportPending = isReportPending,
                onReportClick = onReportClick,
            )
        } else if (hasReportActions) {
            Spacer(Modifier.height(14.dp))
            ReportButton(onClick = onReportClick, enabled = !isReportPending)
        }

        if (lookalikes.isNotEmpty()) {
            Spacer(Modifier.height(14.dp))
            LookalikeRow(lookalikes)
        }

        Spacer(Modifier.height(12.dp))
        // Mention permanente, inconditionnelle (ADR 0015) : l'absence d'avertissement plus haut
        // signifie « non documenté », jamais « sûr » — cette phrase le rappelle sur CHAQUE fiche.
        Text(
            text = stringResource(Res.string.treedetail_confidence_disclaimer),
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

/**
 * Actions du propriétaire : la ligne « c'est ton arbre », plus la feuille réduite si contesté.
 *
 * La ligne a deux formulations parce qu'un bouton « Signaler… » s'affiche juste en dessous quand
 * l'identification est contestée, et que ce bouton n'ouvre alors qu'une seule action, « Maintenir
 * mon identification ». Une phrase unique qui dirait « tu ne peux pas le signaler » se
 * contredirait a l'ecran. Conditionner la ligne a son absence la ferait disparaitre au moment ou
 * elle est la plus utile, celui ou l'arbre est conteste.
 */
@Composable
private fun OwnerActions(
    hasReportActions: Boolean,
    isReportPending: Boolean,
    onReportClick: () -> Unit,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
    ) {
        Icon(
            imageVector = Icons.Outlined.Person,
            contentDescription = null,
            modifier = Modifier.size(20.dp),
            tint = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Text(
            text =
                if (hasReportActions) {
                    stringResource(Res.string.treedetail_confidence_owner_note_disputed)
                } else {
                    stringResource(Res.string.treedetail_confidence_owner_note)
                },
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
    // Contesté (spec C1/C3) : la seule action restante du propriétaire vit dans la même
    // feuille « Signaler… », réduite à « Maintenir mon identification ».
    if (hasReportActions) {
        Spacer(Modifier.height(FruxSpacing.sm))
        ReportButton(onClick = onReportClick, enabled = !isReportPending)
    }
}

/** Jauge 3 segments : certaine 3, probable 2, à confirmer 1 (spec zone Confiance). */
@Composable
private fun CertaintyGauge(certainty: Certainty) {
    val filled =
        when (certainty) {
            Certainty.CERTAIN -> 3
            Certainty.PROBABLE -> 2
            Certainty.TO_CONFIRM -> 1
        }
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        repeat(3) { index ->
            Box(
                modifier =
                    Modifier
                        .weight(1f)
                        .height(6.dp)
                        .clip(RoundedCornerShape(3.dp))
                        .background(
                            if (index < filled) {
                                MaterialTheme.fruxColors.statusAvailable
                            } else {
                                MaterialTheme.colorScheme.primary.copy(alpha = 0.12f)
                            },
                        ),
            )
        }
    }
}

/** Bouton « Signaler… » : surface blanche, bordure `outlineVariant`, pill 48 dp (spec zone 2). */
@Composable
private fun ReportButton(
    onClick: () -> Unit,
    enabled: Boolean,
) {
    Surface(
        onClick = onClick,
        enabled = enabled,
        shape = RoundedCornerShape(50),
        color = MaterialTheme.colorScheme.surface,
        contentColor = MaterialTheme.colorScheme.primary,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        modifier = Modifier.height(48.dp),
    ) {
        Row(
            modifier = Modifier.padding(horizontal = FruxSpacing.md),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
        ) {
            Icon(
                imageVector = Icons.Outlined.Flag,
                contentDescription = null,
                modifier = Modifier.size(18.dp),
            )
            Text(
                stringResource(Res.string.treedetail_confidence_report_cta),
                style = MaterialTheme.typography.labelLarge,
            )
        }
    }
}

/**
 * Niveau 3 (ADR 0015) : sosie(s) documenté(s) avec une identification jugée sûre — ligne repliée
 * par défaut, carte `surface` dans le bloc, palette neutre (jamais rouge, réservé au niveau 4).
 */
@Composable
private fun LookalikeRow(lookalikes: List<SpeciesLookalike>) {
    var expanded by remember { mutableStateOf(false) }
    val uriHandler = LocalUriHandler.current
    Column(
        modifier =
            Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(12.dp))
                .background(MaterialTheme.colorScheme.surface)
                .clickable { expanded = !expanded }
                .padding(12.dp),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            Icon(
                imageVector = Icons.Outlined.Compare,
                contentDescription = null,
                modifier = Modifier.size(20.dp),
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Text(
                text = stringResource(Res.string.treedetail_confidence_lookalike_title),
                style = MaterialTheme.typography.titleSmall,
                modifier = Modifier.weight(1f),
            )
            Icon(
                imageVector = if (expanded) Icons.Outlined.ExpandLess else Icons.Outlined.ExpandMore,
                contentDescription =
                    if (expanded) {
                        stringResource(Res.string.treedetail_confidence_lookalike_collapse)
                    } else {
                        stringResource(Res.string.treedetail_confidence_lookalike_expand)
                    },
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        if (expanded) {
            Spacer(Modifier.height(FruxSpacing.sm))
            lookalikes.forEach { lookalike ->
                Column(modifier = Modifier.padding(bottom = FruxSpacing.sm)) {
                    Text(
                        text = lookalike.commonNameFr,
                        style = MaterialTheme.typography.bodyMedium,
                    )
                    Text(
                        text = lookalike.distinguishingNote,
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                    Text(
                        text = lookalike.sourceUrl,
                        style = MaterialTheme.typography.bodySmall.copy(textDecoration = TextDecoration.Underline),
                        color = MaterialTheme.colorScheme.primary,
                        modifier = Modifier.clickable { uriHandler.openUri(lookalike.sourceUrl) },
                    )
                }
            }
        }
    }
}

/** Étiquette de section en capitales (« CONFIANCE »), partagée par les blocs. */
@Composable
internal fun SectionLabel(
    icon: ImageVector,
    text: String,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier,
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
    ) {
        Icon(
            imageVector = icon,
            contentDescription = null,
            modifier = Modifier.size(16.dp),
            tint = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Text(
            text = text.uppercase(),
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}
